import React, { useEffect, useMemo } from "react"
import { Form, InputNumber, Space, Typography, Spin, Flex } from "antd"
import { useSelector, useDispatch } from "react-redux"
import { debounce } from "lodash"
import { getAssetAllocation } from "store/bookings"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"
import PropTypes from "prop-types"
import styles from "./BookingForm.module.css"
const { Text } = Typography

const GuestInputGroup = ({ form, setCounts, disabled, bookingSelections, editingBookingUuid, clientParams }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    experienceBookingData,
    experienceById: { experienceDetail }
  } = useSelector((state) => state.experiences)
  const assetAllocation = useSelector((state) => state.bookings.assetAllocation)
  const { children_allowed, infants_allowed, passengers_allowed } = bookingSelections?.tripLength?.price || {}
  const maxGuests = experienceBookingData?.bookingData?.max_guests
  const minGuests = experienceBookingData?.bookingData?.min_guests

  const maxGuestsLimit = maxGuests || 0
  const experienceId = experienceDetail?.id || bookingSelections?.experience?.id

  // Create debounced function for fetching asset allocation
  const debouncedFetchAssets = useMemo(
    () =>
      debounce((counts, tripId, date, timeSlot, maxGuests, minGuests, autoAssignEquipment) => {
        const mainCounts = counts.main || {}
        const extraCounts = counts.extra || {}

        const adultCount = (mainCounts.adults || 0) + (extraCounts.adults || 0)
        const childCount = (mainCounts.children || 0) + (extraCounts.children || 0)
        const infantCount = (mainCounts.infants || 0) + (extraCounts.infants || 0)
        const passengerCount = (mainCounts.passengers || 0) + (extraCounts.passengers || 0)

        const totalMainGuests = Object.values(mainCounts).reduce((sum, val) => sum + (val || 0), 0)
        const totalGuests = adultCount + childCount + infantCount + passengerCount

        if (!tripId || !date || !timeSlot || totalGuests === 0) return
        if (totalMainGuests > maxGuests || totalMainGuests < minGuests) return

        const params = {
          trip_id: tripId,
          date: date,
          time: timeSlot,
          adult_count: adultCount,
          child_count: childCount,
          infant_count: infantCount,
          passenger_count: passengerCount
        }

        // On edit, tell the backend which booking to exclude from the capacity check — without it the
        // booking's own equipment counts as taken and the preview 422s before anything is changed.
        if (editingBookingUuid) params.b_uuid = editingBookingUuid

        // client_id when the client is known, client_email as the fallback for a brand-new client.
        if (clientParams) Object.assign(params, clientParams)

        if (isFeatureEnabled('NEW_RESOURCES') && autoAssignEquipment) {
          dispatch(getAssetAllocation(params))
        }
      }, 500),
    [dispatch, editingBookingUuid, clientParams]
  )

  const createValidator = (fieldName, sectionKey) => (_, value) => {
    const values = form.getFieldsValue([sectionKey])
    const sectionValues = values[sectionKey] || {}
    sectionValues[fieldName] = value
    const total = Object.values(sectionValues).reduce((sum, v) => sum + (Number(v) || 0), 0)

    if (fieldName === "adults" && (!value || value < 1) && sectionKey === "main") {
      return Promise.reject(new Error(t("booking.error_messages.adults_required")))
    }

    if (value > 0) {
      if (total > maxGuestsLimit && sectionKey === "main") {
        return Promise.reject(new Error(t("booking.error_messages.max_guest_limit", { max: maxGuestsLimit })))
      } else if (total < minGuests && sectionKey === "main") {
        return Promise.reject(new Error(t("booking.error_messages.min_guest_limit", { min: minGuests })))
      }
    }
    return Promise.resolve()
  }

  const updateCount = (section, key, value) => {
    let newCounts = {
      ...bookingSelections?.counts,
      [section]: {
        ...bookingSelections?.counts?.[section],
        [key]: value
      }
    }
    // Logic to clear extra counts if main changes is no longer needed as we only use main

    setCounts(newCounts)
  }

  useEffect(() => {
    const counts = bookingSelections?.counts
    if (counts) {
      form.setFieldsValue({
        main: counts?.main
      })
    }
  }, [bookingSelections?.counts, form])

  useEffect(() => {
    if (!(minGuests > 0)) return
    // Seed the adult count to the trip's minimum only when no guest count has been chosen yet —
    // never overwrite an existing selection. Guard on the persisted selection (bookingSelections),
    // not the form field: on a step-2 → step-1 round trip the field can momentarily be empty on
    // remount, so guarding on it would re-fire and clobber the user's choice. Seed `minGuests` (the
    // currently selected trip's minimum), not the original booking's adult_count — otherwise
    // switching to an experience with a lower minimum re-picks the old count (e.g. 3 for a trip whose
    // minimum is 2).
    if (bookingSelections?.counts?.main?.adults) return
    form.setFieldsValue({ main: { ...form.getFieldValue("main"), adults: minGuests } })
    setCounts({
      ...bookingSelections?.counts,
      main: { ...bookingSelections?.counts?.main, adults: minGuests }
    })
    // Only run when minGuests changes
    // eslint-disable-next-line
  }, [minGuests])

  useEffect(() => {
    // Only fetch if there are valid guest counts and no validation errors
    const counts = bookingSelections?.counts
    const date = bookingSelections?.date
    const timeSlot = bookingSelections?.timeSlot

    if (counts && experienceId && date && timeSlot) {
      const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)

      // Additional validation: check guest count limits
      const mainCounts = counts.main || {}
      const totalMainGuests = Object.values(mainCounts).reduce((sum, val) => sum + (val || 0), 0)

      // Don't call API if there are form errors or validation fails
      const isMainGuestValid = totalMainGuests >= minGuests && totalMainGuests <= maxGuests

      if (!hasErrors && isMainGuestValid) {
        debouncedFetchAssets(counts, experienceId, date, timeSlot, maxGuests, minGuests, experienceDetail?.auto_assign_equipment)
      }
    }

    // Cleanup function to cancel pending debounced calls on unmount
    return () => {
      debouncedFetchAssets.cancel()
    }
    // eslint-disable-next-line
  }, [
    bookingSelections?.counts,
    experienceId,
    bookingSelections?.date,
    bookingSelections?.timeSlot,
    debouncedFetchAssets,
    maxGuests,
    minGuests
  ])

  const renderGuestInputBlock = (sectionLabel, sectionKey) => (
    <>
      {sectionLabel && (
        <div className="mb-sm">
          <Text className={styles.sectionLabel}>{sectionLabel}</Text>
        </div>
      )}
      <Space size="small" className={styles.inputGroup}>
        {[
          { label: t("booking.labels.adults"), key: "adults", allowed: true },
          { label: t("booking.labels.children"), key: "children", allowed: children_allowed },
          { label: t("booking.labels.infants"), key: "infants", allowed: infants_allowed },
          { label: t("booking.labels.passengers"), key: "passengers", allowed: passengers_allowed }
        ].map(({ label, key, allowed }) => (
          <Form.Item
            key={sectionKey + key}
            label={label}
            name={[sectionKey, key]} // nested form field path
            rules={[{ validator: createValidator(key, sectionKey) }]}
          >
            <InputNumber
              min={0}
              disabled={!allowed || disabled}
              className={styles.inputNumber}
              onChange={(val) => updateCount(sectionKey, key, val)}
            />
          </Form.Item>
        ))}
      </Space>
    </>
  )

  return (
    <>
      {renderGuestInputBlock("", "main")}
      {assetAllocation.loading && (
        <Flex align="center" gap={8} className="py-xs">
          <Spin size="small" />
          <Text>Calculating asset allocation...</Text>
        </Flex>
      )}
      {assetAllocation.count !== null && !assetAllocation.loading && !assetAllocation.error && (
        <Flex className="py-xs">
          <Text>
            {assetAllocation.count} {assetAllocation.count === 1 ? "asset" : "assets"} will be allocated for this booking based on selected guests and asset capacity
          </Text>
        </Flex>
      )}
      {assetAllocation.error && !assetAllocation.loading && (
        <Flex className="py-xs">
          <Text type="danger">
            {assetAllocation.errorMessage || "Insufficient asset capacity for the selected guests and time slot."}
          </Text>
        </Flex>
      )}
    </>
  )
}

GuestInputGroup.propTypes = {
  form: PropTypes.object.isRequired,
  setCounts: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  bookingSelections: PropTypes.object,
  editingBookingUuid: PropTypes.string,
  clientParams: PropTypes.shape({ client_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), client_email: PropTypes.string })
}

export default GuestInputGroup
